import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import api from "../utils/api";
import { buildMachineColorMap } from "../utils/machineColors";
import { getClientMachines } from "../controllers/machineController";

const historicalCache = new Map();
const summaryCache = new Map();
const inFlightHistoricalRequests = new Map();
const inFlightSummaryRequests = new Map();

export const METRIC_API_MAP = {
  power_kw: "power_kw",
  power: "power_kw",
  kW: "power_kw",
  kw: "power_kw",
  "[kw]": "power_kw",
  voltage: "voltage",
  v: "voltage",
  current: "current",
  i: "current",
  power_factor: "power_factor",
  pf: "power_factor",
  frequency: "frequency",
  hz: "frequency",
  THD_V: "thd_voltage",
  thd_v: "thd_voltage",
  thd_voltage: "thd_voltage",
  thdv: "thd_voltage",
  thd: "thd_voltage",
  THD_I: "thd_current",
  thd_i: "thd_current",
  thd_current: "thd_current",
  thdi: "thd_current",
  TAP: "tap",
  tap: "tap",
  energy: "energy",
};

const MODES = {
  minute: {
    label: "Minute",
    granularity: "minute",
    chartType: "line",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  hour: {
    label: "Hour",
    granularity: "hour",
    chartType: "line",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },

  day: {
    label: "Day",
    granularity: "day",
    chartType: "bar",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  month: {
    label: "Month",
    granularity: "month",
    chartType: "bar",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  year: {
    label: "Year",
    granularity: "year",
    chartType: "bar",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  second: {
    label: "Second",
    granularity: "second",
    chartType: "line",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
};

export const useHistoricalData = (selectedBranch = null) => {
  const [mode, setMode] = useState("hour");
  const [selectedMetric, setSelectedMetric] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);
  const rawResponseRef = useRef(null);
  useEffect(() => {
    rawResponseRef.current = rawResponse;
  }, [rawResponse]);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [avgMonthlyPfData, setAvgMonthlyPfData] = useState(null);
  const [avgMonthlyPfLoading, setAvgMonthlyPfLoading] = useState(false);
  const [mdiData, setMdiData] = useState(null);
  const [mdiLoading, setMdiLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [machineColors, setMachineColors] = useState({});
  const [selectedMachineNode, setSelectedMachineNode] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    end.setDate(end.getDate() - 1); // Yesterday
    end.setHours(23, 59, 59, 999);

    const start = new Date(end);
    start.setDate(end.getDate() - 6); // 7 days inclusive of yesterday (start to end = 7 days)
    start.setHours(0, 0, 0, 0);

    return { start, end };
  });

  // Reset fetch deduplication when mode changes
  useEffect(() => {
    // When mode changes, force a fresh fetch by clearing lastFetchParams
    lastFetchParams.current = { mode: null, start: null, end: null, selected: null, metric: null, branch: null };
    lastSummaryFetchParams.current = { machineName: null, start: null, end: null, metric: null, mode: null, branch: null };
    console.log(
      "[Effect] Mode changed to:",
      mode,
      "- clearing deduplication cache",
    );
  }, [mode]);

  // Generation counters. A newer fetch must not abort the one already on the
  // wire — Chrome shows that as (canceled). Stale responses are ignored instead.
  const historicalRequestId = useRef(0);
  const summaryRequestId = useRef(0);
  const lastFetchParams = useRef({ mode: null, start: null, end: null, selected: null, metric: null, branch: null });
  const lastSummaryFetchParams = useRef({ machineName: null, start: null, end: null, metric: null, mode: null, branch: null });
  const prevFetchRef = useRef({ start: null, end: null, mode: null, selected: null, metric: null, branch: null });

  const [graphDateRange, setGraphDateRange] = useState(() => {
    const end = new Date();
    end.setDate(end.getDate() - 1); // Yesterday
    end.setHours(23, 59, 59, 999);

    const start = new Date(end);
    start.setDate(end.getDate() - 6); // 7 days inclusive of yesterday
    start.setHours(0, 0, 0, 0);

    return { start, end };
  });

  const username = useMemo(() => localStorage.getItem("username") || "", []);

  const modeMeta = MODES[mode] || MODES.day;

  const selectedMetricRef = useRef(selectedMetric);
  useEffect(() => {
    selectedMetricRef.current = selectedMetric;
  }, [selectedMetric]);

  const fetchSummaryData = useCallback(
    async (machineName, comparisonMetric, branchOverride) => {
      if (!username) return;

      const targetBranch = branchOverride !== undefined ? branchOverride : selectedBranch;
      const targetMachine =
        machineName && machineName !== "ALL" && machineName !== "__ALL_MACHINES__"
          ? machineName
          : selectedMachineNode?.name || "Usage";

      const apiMetric =
        METRIC_API_MAP[comparisonMetric] ||
        METRIC_API_MAP[comparisonMetric?.toLowerCase()] ||
        "power_kw";

      const startStr = formatDate(graphDateRange.start);
      const endStr = formatDate(graphDateRange.end);
      const branchKey = targetBranch && targetBranch !== "All" ? targetBranch : null;

      // Deduplication check for summary
      if (
        lastSummaryFetchParams.current.machineName === targetMachine &&
        lastSummaryFetchParams.current.start === startStr &&
        lastSummaryFetchParams.current.end === endStr &&
        lastSummaryFetchParams.current.metric === apiMetric &&
        lastSummaryFetchParams.current.mode === mode &&
        lastSummaryFetchParams.current.branch === branchKey
      ) {
        return;
      }
      lastSummaryFetchParams.current = {
        machineName: targetMachine,
        start: startStr,
        end: endStr,
        metric: apiMetric,
        mode,
        branch: branchKey,
      };

      const params = new URLSearchParams({
        username,
        start: startStr,
        end: endStr,
        mode,
        machine_name: targetMachine,
        metric: apiMetric,
      });
      if (branchKey) {
        params.append("branch", branchKey);
      }

      const queryString = params.toString();
      const requestId = ++summaryRequestId.current;
      try {
        let summaryResult;
        if (summaryCache.has(queryString)) {
          summaryResult = summaryCache.get(queryString);
        } else if (inFlightSummaryRequests.has(queryString)) {
          setSummaryLoading(true);
          setAvgMonthlyPfLoading(true);
          setMdiLoading(true);
          summaryResult = await inFlightSummaryRequests.get(queryString);
        } else {
          setSummaryLoading(true);
          setAvgMonthlyPfLoading(true);
          setMdiLoading(true);

          const reqPromise = api
            .get(`/historical_summary?${queryString}`, {
              timeout: 0,
            })
            .then((res) => {
              summaryCache.set(queryString, res.data);
              return res.data;
            })
            .finally(() => {
              inFlightSummaryRequests.delete(queryString);
            });

          inFlightSummaryRequests.set(queryString, reqPromise);
          summaryResult = await reqPromise;
        }

        if (requestId !== summaryRequestId.current) return;

        if (summaryResult) {
          setSummaryData(summaryResult.summary || null);
          setAvgMonthlyPfData(summaryResult.lpf || null);
          setMdiData(summaryResult.mdi || null);
        }
      } catch (err) {
        if (requestId !== summaryRequestId.current) return;
        if (
          err.name === "AbortError" ||
          err.message === "canceled" ||
          err.code === "ERR_CANCELED"
        ) {
          return;
        }
        console.error("fetchSummaryData error:", err);
      } finally {
        if (requestId === summaryRequestId.current) {
          setSummaryLoading(false);
          setAvgMonthlyPfLoading(false);
          setMdiLoading(false);
        }
      }
    },
    [username, selectedBranch, selectedMachineNode?.name, graphDateRange.start, graphDateRange.end, mode]
  );

  const fetchData = useCallback(
    async (selectedMachinesParam, metricParam, branchOverride, forceRefresh = false) => {
    if (!username) {
      setError("Missing username. Please log in again.");
      return;
    }

    // Guard: second mode max 24h; minute mode max 24h sliding window
    if (mode === "second" || mode === "minute") {
      const diffMs = Math.abs(
        graphDateRange.end.getTime() - graphDateRange.start.getTime(),
      );
      const maxSpanMs = 24 * 60 * 60 * 1000 + 60000;
      if (diffMs > maxSpanMs) {
        console.log(
          `Skipping fetchData: ${mode} mode range > 24h`,
          diffMs,
        );
        return;
      }
    }

    let requestId = 0;
    try {
      const startStr = formatDate(graphDateRange.start);
      const endStr = formatDate(graphDateRange.end);
      const targetBranch = branchOverride !== undefined ? branchOverride : selectedBranch;
      const branchKey = targetBranch && targetBranch !== "All" ? targetBranch : null;

      console.log("[useHistoricalData] fetchData called", { selectedMachinesParam, metricParam, mode, start: startStr, end: endStr, branch: branchKey });

      // Determine resolved metric before dedup check so metric is considered
      const currentSelectedMetric = selectedMetricRef.current;
      const rawMetric = metricParam
        || (currentSelectedMetric ? currentSelectedMetric.split("_").slice(1).join("_") : null)
        || "power_kw";
      const resolvedMetric =
        METRIC_API_MAP[rawMetric] ||
        METRIC_API_MAP[rawMetric?.toLowerCase()] ||
        rawMetric;

      // Deduplication check - include selected machines, metric and branch
      const selKey = selectedMachinesParam || null;
      const metricKey = resolvedMetric || null;

      if (
        lastFetchParams.current.mode === mode &&
        lastFetchParams.current.start === startStr &&
        lastFetchParams.current.end === endStr &&
        lastFetchParams.current.selected === selKey &&
        lastFetchParams.current.metric === metricKey &&
        lastFetchParams.current.branch === branchKey
      ) {
        console.log("Skipping redundant fetch for:", { mode, start: startStr, selected: selKey, metric: metricKey, branch: branchKey });
        return;
      }

      // Check if only the machine changed within the SAME branch, metric, and date/mode
      const isSameBranch = lastFetchParams.current.branch === branchKey;
      const isSameMetric = lastFetchParams.current.metric === metricKey;
      const isOnlyMachineChange =
        lastFetchParams.current.mode === mode &&
        lastFetchParams.current.start === startStr &&
        lastFetchParams.current.end === endStr &&
        isSameBranch &&
        isSameMetric;

      // Update LAST PARAMS and prevFetchRef early to prevent duplicate calls from effect or aborting
      lastFetchParams.current = { mode, start: startStr, end: endStr, selected: selKey, metric: metricKey, branch: branchKey };
      prevFetchRef.current = {
        start: graphDateRange.start ? graphDateRange.start.getTime() : null,
        end: graphDateRange.end ? graphDateRange.end.getTime() : null,
        mode,
        selected: selKey,
        metric: metricKey,
        branch: branchKey,
      };
      if (!isOnlyMachineChange || !rawResponseRef.current) {
        setLoading(true);
      }
      setError(null);

      const targetMachine =
        selectedMachinesParam && selectedMachinesParam !== "ALL" && selectedMachinesParam !== "__ALL_MACHINES__"
          ? selectedMachinesParam
          : selectedMachineNode?.name || "Usage";

      // A single machine passed into this call is the machine the user just
      // picked. selectedMachineNode is still the previous one here (Usage),
      // because setState has not committed yet — using it sent /historical_summary
      // for Usage while /historical_data was already for New Lift.
      const singleSelectedMachine =
        selectedMachinesParam &&
        selectedMachinesParam !== "ALL" &&
        selectedMachinesParam !== "__ALL_MACHINES__" &&
        !selectedMachinesParam.includes(",");
      const primarySummaryMachine = singleSelectedMachine
        ? selectedMachinesParam
        : (selectedMachineNode?.name && selectedMachineNode.name !== "__ALL_MACHINES__"
            ? selectedMachineNode.name
            : "Usage");

      // Fire summary request concurrently without blocking line/bar timeseries
      fetchSummaryData(primarySummaryMachine, resolvedMetric, targetBranch);

      // Check if data for targetMachine and resolvedMetric is already present in rawResponseRef.current
      // NOTE: Only valid if branch AND metric have NOT changed, because different metrics require different series from backend!
      if (isOnlyMachineChange && isSameBranch && isSameMetric && rawResponseRef.current && !forceRefresh) {
        const existingData = rawResponseRef.current;
        const isUsageTarget = targetMachine === "Usage" || targetMachine === "ALL";

        let machineFound = false;
        if (isUsageTarget) {
          machineFound = Boolean(existingData.machines || existingData["Date & Time"] || existingData.Usage);
        } else if (existingData.machines) {
          const match = Object.values(existingData.machines).find(
            (m) =>
              (m.name || "").toLowerCase() === targetMachine.toLowerCase() ||
              (m.id || "").toLowerCase() === targetMachine.toLowerCase()
          );
          if (match && match.data) {
            const metricLower = resolvedMetric.toLowerCase();
            const availableMetrics = Object.keys(match.data).map((k) => k.toLowerCase());
            machineFound =
              availableMetrics.length > 0 &&
              availableMetrics.some(
                (k) =>
                  k.includes(metricLower) ||
                  metricLower.includes(k) ||
                  (metricLower === "power_kw" && (k.includes("kw") || k === "[kw]"))
              );
          }
        }

        if (machineFound) {
          console.log(
            `[useHistoricalData] In-memory data found for ${targetMachine} (${resolvedMetric}), skipping /historical_data API call.`
          );
          setLoading(false);
          return;
        }
      }

      const isAllSelected =
        selectedMachinesParam === "ALL" ||
        selectedMachinesParam === "__ALL_MACHINES__";

      // Only the machines the user actually selected. Adding Usage here made
      // every New Lift chart request also run the whole-facility query.
      let selectedMachinesQuery = isAllSelected ? "ALL" : (targetMachine || "Usage");
      if (!isAllSelected && selectedMachinesParam && typeof selectedMachinesParam === "string") {
        const parts = selectedMachinesParam.split(",").map((s) => s.trim()).filter(Boolean);
        if (parts) selectedMachinesQuery = parts.join(",");
      }

      const params = new URLSearchParams({
        username,
        mode,
        start: startStr,
        end: endStr,
        machine_name: targetMachine,
        selected_machines: selectedMachinesQuery,
      });

      // Metric filter applied to API to return only requested metric series
      if (resolvedMetric) params.append("metric", resolvedMetric);
      if (branchKey) params.append("branch", branchKey);

      const queryString = params.toString();
      let bundle;
      requestId = ++historicalRequestId.current;

      if (historicalCache.has(queryString)) {
        bundle = historicalCache.get(queryString);
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else if (inFlightHistoricalRequests.has(queryString)) {
        bundle = await inFlightHistoricalRequests.get(queryString);
      } else {
        // No client timeout and no AbortSignal. A 60s axios timeout aborts the
        // XHR, which Chrome reports as (canceled) at the same moment nginx
        // closes a slow query. Identical in-flight calls share reqPromise above.
        const reqPromise = (async () => {
          let resData;
          try {
            const response = await api.get(`/historical_data?${queryString}`, {
              timeout: 0,
            });
            resData = response.data;
          } catch (dataErr) {
            if (
              dataErr.name === "AbortError" ||
              dataErr.message === "canceled" ||
              dataErr.code === "ERR_CANCELED"
            ) {
              throw dataErr;
            }
            // Only an old server without /historical_data should hit the bundle.
            // A timeout or 504 means this query is already too slow; the bundle
            // runs it again and the extra load is what gets the other Historical
            // calls aborted.
            if (dataErr?.response?.status !== 404) {
              throw dataErr;
            }
            console.warn("Falling back to historical_bundle endpoint:", dataErr);
            const fallbackRes = await api.get(`/historical_bundle?${queryString}`, {
              timeout: 0,
            });
            resData = fallbackRes.data;
          }
          historicalCache.set(queryString, resData);
          return resData;
        })().finally(() => {
          inFlightHistoricalRequests.delete(queryString);
        });

        inFlightHistoricalRequests.set(queryString, reqPromise);
        bundle = await reqPromise;
      }

      if (requestId !== historicalRequestId.current) return;

      const resData = bundle?.data?.data || bundle?.data || bundle;
      setRawResponse(resData || null);

      if (bundle?.summary) setSummaryData(bundle.summary);
      if (bundle?.lpf) setAvgMonthlyPfData(bundle.lpf);
      if (bundle?.mdi) setMdiData(bundle.mdi);

      // Prefer `Hierarchy` for building the machine selector (full list of machines).
      if (resData?.Hierarchy) {
        const parsedTree = parseHierarchy(resData.Hierarchy);
        setHierarchyData(parsedTree);

        // Build machine colors if not yet built
        const hierarchyColorMap = {};
        Object.entries(resData.Hierarchy).forEach(([name, meta]) => {
          if (meta && typeof meta === "object" && meta.color) {
            hierarchyColorMap[name] = meta.color;
          }
        });

        getClientMachines(username)
          .then((clientData) => {
            const clientMachines = clientData?.machines || [];
            const clientMachineNames = new Set(clientMachines.map((m) => m.machine_name || m.name));
            const hierarchyOnlyNodes = Object.entries(resData.Hierarchy)
              .filter(([name]) => !clientMachineNames.has(name))
              .map(([name, meta]) => ({
                machine_name: name,
                color: (meta && typeof meta === "object") ? meta.color : null,
              }));
            const allNodes = [...clientMachines, ...hierarchyOnlyNodes];
            const fullColorMap = buildMachineColorMap(allNodes, hierarchyColorMap);
            setMachineColors((prevColors) => {
              if (Object.keys(prevColors).length > 0) return prevColors;
              return fullColorMap;
            });
          })
          .catch(() => { });
      }
    } catch (err) {
      if (requestId !== historicalRequestId.current) return;
      if (
        err.name === "AbortError" ||
        err.message === "canceled" ||
        err.code === "ERR_CANCELED"
      ) {
        console.log("Fetch aborted/canceled - ignoring error state update");
        return;
      }
      console.error("Fetch error:", err);
      // Reset lastFetchParams on error to allow retries
      lastFetchParams.current = { mode: null, start: null, end: null, selected: null, metric: null, branch: null };
      setError(err.message || "Unable to load data");
    } finally {
      if (requestId === historicalRequestId.current) {
        setLoading(false);
      }
    }
  }, [username, mode, graphDateRange.start, graphDateRange.end, selectedMachineNode?.name, selectedBranch, fetchSummaryData]);

  useEffect(() => {
    // dateRange is what Apply commits and what the other Historical charts use.
    // graphDateRange follows it one effect later. Fetching in that gap sends the
    // old 7-day /historical_data and /historical_summary, which the next effect
    // then aborts (Chrome: canceled) and replaces with the 30-day calls.
    if (mode !== "minute" && mode !== "second") {
      const sameStart = graphDateRange.start?.getTime() === dateRange.start?.getTime();
      const sameEnd = graphDateRange.end?.getTime() === dateRange.end?.getTime();
      if (!sameStart || !sameEnd) return;
    }

    const startTs = graphDateRange.start ? graphDateRange.start.getTime() : null;
    const endTs = graphDateRange.end ? graphDateRange.end.getTime() : null;

    const sel = selectedMachineNode?.name || "Usage";
    // Series keys look like "New Lift_kW". Splitting on the first "_" kept
    // "kW", which is not power_kw, so the effect fired a second /historical_data.
    const metricParam = metricFromSeriesKey(selectedMetricRef.current);
    const branchKey = selectedBranch && selectedBranch !== "All" ? selectedBranch : null;

    if (
      prevFetchRef.current.start === startTs &&
      prevFetchRef.current.end === endTs &&
      prevFetchRef.current.mode === mode &&
      prevFetchRef.current.selected === sel &&
      prevFetchRef.current.metric === metricParam &&
      prevFetchRef.current.branch === branchKey
    ) {
      return;
    }

    prevFetchRef.current = { start: startTs, end: endTs, mode, selected: sel, metric: metricParam, branch: branchKey };
    fetchData(sel, metricParam, branchKey);
  }, [graphDateRange.start, graphDateRange.end, dateRange.start, dateRange.end, mode, selectedMachineNode, selectedBranch, fetchData]);



  // Set initial range when mode changes (layout phase so graphDateRange is
  // narrowed before the fetch useEffect runs; avoids minute/second guard skip
  // while graphDateRange still reflects the previous mode's wide span)
  const lastModeRef = useRef(mode);

  useLayoutEffect(() => {
    const modeChanged = lastModeRef.current !== mode;
    if (!modeChanged) return;

    const previousMode = lastModeRef.current;
    lastModeRef.current = mode;

    const end = new Date();
    if (mode !== "second") {
      end.setSeconds(0, 0);
    }

    if (mode === "second") {
      // For second mode: always reset to 1 hour back from current time
      const start = new Date(end.getTime() - 60 * 60 * 1000); // 1 hour
      console.log("[Mode Change] Second mode - setting 1-hour range", {
        start: formatDate(start),
        end: formatDate(end),
      });
      setGraphDateRange({ start, end });
    } else if (mode === "minute") {
      // Sliding window: last 24 hours ending at current time (minute resolution)
      const rangeEnd = new Date(end);
      rangeEnd.setSeconds(0, 0);
      const rangeStart = new Date(
        rangeEnd.getTime() - 24 * 60 * 60 * 1000,
      );
      console.log("[Mode Change] Minute mode - 24h sliding window", {
        start: formatDate(rangeStart),
        end: formatDate(rangeEnd),
      });
      setGraphDateRange({ start: rangeStart, end: rangeEnd });
      setDateRange({ start: rangeStart, end: rangeEnd });
    } else {
      // For hour/day/month/year modes
      // If coming from minute or second mode, reset to 7 days
      if (previousMode === "minute" || previousMode === "second") {
        const { start: rStart, end: rEnd } = getRelativeRange(7);
        console.log("[Mode Change] Switching from minute/second to", mode, {
          start: formatDate(rStart),
          end: formatDate(rEnd),
        });
        setDateRange({ start: rStart, end: rEnd });
        setGraphDateRange({ start: rStart, end: rEnd });
      } else {
        // Otherwise, sync graphDateRange with current dateRange
        console.log("[Mode Change]", mode, "- syncing with dateRange");
        setGraphDateRange({ start: dateRange.start, end: dateRange.end });
      }
    }
  }, [mode, dateRange.start, dateRange.end]);

  // Sync graphDateRange when dateRange changes in non-isolated modes
  useEffect(() => {
    if (mode !== "minute" && mode !== "second") {
      setGraphDateRange({ start: dateRange.start, end: dateRange.end });
    }
  }, [dateRange, mode]);

  const setGraphDateRangeFromStrings = ({ start, end }) => {
    const parsedStart = start ? new Date(start) : null;
    const parsedEnd = end ? new Date(end) : null;
    if (
      !parsedStart ||
      !parsedEnd ||
      Number.isNaN(parsedStart) ||
      Number.isNaN(parsedEnd)
    )
      return;
    // Logical check: start <= end
    if (parsedStart > parsedEnd) return;

    const commitGraph = (s, e) => {
      setGraphDateRange({ start: s, end: e });
      if (mode === "minute") {
        setDateRange({ start: s, end: e });
      }
    };

    // Minute mode: max span 24 hours (can cross calendar midnight)
    if (mode === "minute") {
      const diffMs = parsedEnd.getTime() - parsedStart.getTime();
      const maxSpanMs = 24 * 60 * 60 * 1000;
      if (diffMs > maxSpanMs) {
        const adjustedEnd = new Date(parsedStart.getTime() + maxSpanMs);
        commitGraph(parsedStart, adjustedEnd);
        return;
      }
    }

    // For second mode, ensure range is within 1 hour
    if (mode === "second") {
      const diffMs = parsedEnd.getTime() - parsedStart.getTime();
      const oneHourMs = 60 * 60 * 1000;
      if (diffMs > oneHourMs) {
        // Adjust end to be 1 hour from start
        const adjustedEnd = new Date(parsedStart.getTime() + oneHourMs);
        setGraphDateRange({ start: parsedStart, end: adjustedEnd });
        return;
      }
    }

    commitGraph(parsedStart, parsedEnd);
  };

  const timeLabels = useMemo(() => {
    const source = rawResponse?.["Date & Time"];
    if (!source) return [];
    if (Array.isArray(source)) return source;
    if (typeof source === "object") {
      return Object.values(source).filter(Boolean);
    }
    return [source];
  }, [rawResponse]);

  // Derived indices after DateTime filtering
  const filteredIndices = useMemo(() => {
    const indices = [];
    const startTimeToken = graphDateRange.start.getTime();
    const endTimeToken = graphDateRange.end.getTime();

    // Buffer to handle slight mismatches
    const buffer = 5000;

    for (let i = 0; i < timeLabels.length; i++) {
      const label = timeLabels[i];
      if (!label) continue;

      const date = new Date(label);
      if (Number.isNaN(date.getTime())) {
        // ⚠️ CRITICAL: Skip invalid dates to prevent ApexCharts "parser Error"
        continue;
      }

      const ts = date.getTime();
      if (ts >= startTimeToken - buffer && ts <= endTimeToken + buffer) {
        indices.push(i);
      }
    }

    return indices;
  }, [timeLabels, graphDateRange]);

  // Unified indices after decimation
  const decimatedIndices = useMemo(() => {
    if (!filteredIndices.length) return [];

    const MAX_POINTS = 2000;
    if (filteredIndices.length <= MAX_POINTS) return filteredIndices;

    const step = Math.ceil(filteredIndices.length / MAX_POINTS);
    return filteredIndices.filter((_, idx) => idx % step === 0);
  }, [filteredIndices]);

  const seriesByMetric = useMemo(() => {
    if (!rawResponse) return {};
    const result = {};
    const usageTotals = [];

    const processValues = (rawValues) => {
      let values = rawValues;
      if (!Array.isArray(values)) {
        if (values && typeof values === "object") {
          values = Object.values(values);
        } else if (values !== undefined && values !== null) {
          values = [values];
        } else {
          values = [];
        }
      }
      return values.map((value) => {
        if (typeof value === "number") return value;
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
      });
    };

    // Determine which machines contribute to Usage
    const hasGrid = hierarchyData && Array.isArray(hierarchyData) && hierarchyData.some(
      (node) => (node.category || "").toUpperCase() === "GRID"
    );

    const machinesToSum = new Set();
    if (hasGrid) {
      hierarchyData.forEach((node) => {
        if ((node.category || "").toUpperCase() === "GRID") {
          machinesToSum.add(node.name);
        }
      });
    } else if (hierarchyData && Array.isArray(hierarchyData)) {
      hierarchyData.forEach((node) => {
        if ((node.category || "").toUpperCase() === "MACHINE") {
          machinesToSum.add(node.name);
        }
      });
    }

    // Handle New Nested Structure
    if (rawResponse.machines) {
      Object.entries(rawResponse.machines).forEach(([id, machine]) => {
        const machineName = machine.name || id;
        if (machine.data) {
          Object.entries(machine.data).forEach(([metricName, rawValues]) => {
            const compositeKey = `${machineName}_${metricName}`;
            const parsedValues = processValues(rawValues);

            // For Usage aggregate we only want the positive power series (kW)
            // Accept backend variants like `power_kw` or `kW`, but explicitly
            // exclude negative/offset series such as `kW_neg`.
            const isPowerKw = /^power[_-]?kw$/i.test(metricName) || /^kw(?!h)$/i.test(metricName) || /^\[kw\]$/i.test(metricName);
            const isNeg = /neg/i.test(metricName) || /_neg$/i.test(metricName);
            if (machinesToSum.has(machineName) && isPowerKw && !isNeg) {
              parsedValues.forEach((value, index) => {
                if (value == null) return;
                usageTotals[index] = (usageTotals[index] || 0) + value;
              });
            }

            // Apply filtering and decimation using the unified decimatedIndices
            const filteredValues = decimatedIndices
              .filter((idx) => idx < parsedValues.length)
              .map((idx) => parsedValues[idx]);

            result[compositeKey] = filteredValues;
            if (machineName.toLowerCase().startsWith("usage")) {
              result[`Usage_${metricName}`] = filteredValues;
            }
          });
        }
      });
    } else {
      // Legacy structure handling
      Object.entries(rawResponse).forEach(([key, rawValues]) => {
        if (key === "Date & Time") return;
        const parsedValues = processValues(rawValues);

        // Apply filtering and decimation using the unified decimatedIndices
        const filteredValues = decimatedIndices
          .filter((idx) => idx < parsedValues.length)
          .map((idx) => parsedValues[idx]);

        result[key] = filteredValues;
      });
    }

    if (usageTotals.length && !result["Usage_[kW]"] && !result["Usage_power_kw"] && !result["Usage_kW"]) {
      result.Usage_power_kw = decimatedIndices
        .filter((idx) => idx < usageTotals.length)
        .map((idx) => usageTotals[idx]);
    }

    return result;
  }, [rawResponse, decimatedIndices, hierarchyData]);

  const metrics = useMemo(() => Object.keys(seriesByMetric), [seriesByMetric]);

  useEffect(() => {
    if (!metrics.length) {
      if (selectedMetric !== "") {
        setSelectedMetric("");
      }
      return;
    }
    if (!selectedMetric || !metrics.includes(selectedMetric)) {
      const usageKey = metrics.find((m) => m.startsWith("Usage_"));
      if (usageKey) {
        setSelectedMetric(usageKey);
        return;
      }
      setSelectedMetric(metrics[0]);
    }
  }, [metrics, selectedMetric]);

  const displayLabels = useMemo(() => {
    if (!timeLabels.length) return [];

    // Apply decimation to labels using the unified decimatedIndices
    return decimatedIndices.map((idx) =>
      formatDisplayLabel(timeLabels[idx], mode),
    );
  }, [timeLabels, decimatedIndices, mode]);

  const chartData = useMemo(() => {
    return {
      labels: displayLabels,
      data: selectedMetric ? (seriesByMetric[selectedMetric] || []) : [],
    };
  }, [displayLabels, selectedMetric, seriesByMetric]);

  const stats = useMemo(() => {
    if (!chartData.data.length) return null;
    const numbers = chartData.data
      .map((value) => (typeof value === "number" ? value : parseFloat(value)))
      .filter((value) => Number.isFinite(value));

    if (!numbers.length) return null;

    const sum = numbers.reduce((acc, val) => acc + val, 0);
    const avg = sum / numbers.length;
    const min = Math.min(...numbers);
    const max = Math.max(...numbers);
    const delta = numbers[numbers.length - 1] - numbers[0];

    const minIdx = chartData.data.findIndex((v) => Number(v) === min);
    const maxIdx = chartData.data.findIndex((v) => Number(v) === max);

    const minRawTs = minIdx >= 0 && decimatedIndices[minIdx] !== undefined ? timeLabels[decimatedIndices[minIdx]] : null;
    const maxRawTs = maxIdx >= 0 && decimatedIndices[maxIdx] !== undefined ? timeLabels[decimatedIndices[maxIdx]] : null;

    const minTimestamp = minRawTs || (minIdx >= 0 ? chartData.labels[minIdx] : null);
    const maxTimestamp = maxRawTs || (maxIdx >= 0 ? chartData.labels[maxIdx] : null);

    return {
      average: avg,
      min,
      max,
      minTimestamp,
      maxTimestamp,
      latest: numbers[numbers.length - 1],
      change: delta,
    };
  }, [chartData.data, chartData.labels, decimatedIndices, timeLabels]);

  const setDateRangeFromStrings = ({ start, end }) => {
    const parsedStart = start ? new Date(start) : null;
    const parsedEnd = end ? new Date(end) : null;
    if (
      !parsedStart ||
      !parsedEnd ||
      Number.isNaN(parsedStart) ||
      Number.isNaN(parsedEnd)
    )
      return;
    // Logical check: start <= end
    if (parsedStart > parsedEnd) return;
    const next = { start: parsedStart, end: parsedEnd };
    setDateRange(next);
    // Keep the chart range on the same dates in this update. Otherwise the
    // chart fetches the previous range, then cancels it when graphDateRange
    // catches up.
    if (mode !== "minute" && mode !== "second") {
      setGraphDateRange(next);
    }
  };

  const applyQuickRange = (days) => {
    setDateRange(getRelativeRange(days));
  };

  /** Reset the fetch dedup cache so the next fetchData/refetch call always hits the API. */
  const clearFetchCache = useCallback(() => {
    historicalCache.clear();
    summaryCache.clear();
    lastFetchParams.current = { mode: null, start: null, end: null, selected: null, metric: null, branch: null };
    lastSummaryFetchParams.current = { machineName: null, start: null, end: null, metric: null, mode: null, branch: null };
    prevFetchRef.current = { start: null, end: null, mode: null, selected: null, metric: null, branch: null };
  }, []);

  /** Reset the summary dedup cache so the next fetchSummaryData call always hits the API.
   * Call this whenever the selected machine changes to avoid stale-cache skips. */
  const clearSummaryCache = useCallback(() => {
    summaryCache.clear();
    lastSummaryFetchParams.current = { machineName: null, start: null, end: null, metric: null, mode: null, branch: null };
  }, []);

  return {
    mode,
    setMode,
    dateRange,
    graphDateRange,
    setDateRange,
    setDateRangeFromStrings,
    setGraphDateRangeFromStrings,
    selectedMetric,
    setSelectedMetric,
    loading,
    error,
    chartData,
    metrics,
    seriesByMetric,
    modeMeta,
    stats,
    rawLabels: useMemo(() => {
      if (!timeLabels.length) return [];
      return decimatedIndices.map((idx) => timeLabels[idx]);
    }, [timeLabels, decimatedIndices]),
    lastTimestamp: chartData.labels.at?.(-1) || null,
    applyQuickRange,
    refetch: fetchData,
    fetchSummaryData,
    clearSummaryCache,
    clearFetchCache,
    summaryData,
    setSummaryData,
    summaryLoading,
    avgMonthlyPfData,
    avgMonthlyPfLoading,
    mdiData,
    mdiLoading,
    rawResponse,
    machinesMdiData: mdiData || rawResponse?.mdi || null,
    hasData: Boolean(chartData.labels.length && chartData.data.length),
    hierarchyData,
    machineColors,
    selectedMachineNode,
    setSelectedMachineNode,
  };
};

export const getRelativeRange = (days) => {
  const end = new Date();

  // Base logic for historical ranges (exclude today)
  if (days === 1 || days === 3 || days === 7 || days === 30) {
    end.setDate(end.getDate() - 1);
  }

  end.setHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setDate(end.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);

  return { start, end };
};

const SERIES_METRIC_SUFFIXES = [
  ["power_kw", "power_kw"],
  ["power_factor", "power_factor"],
  ["thd_voltage", "thd_voltage"],
  ["thd_current", "thd_current"],
  ["frequency", "frequency"],
  ["voltage", "voltage"],
  ["current", "current"],
  ["energy", "energy"],
  ["kwh", "energy"],
  ["kw", "power_kw"],
  ["pf", "power_factor"],
  ["thdv", "thd_voltage"],
  ["thdi", "thd_current"],
  ["hz", "frequency"],
  ["tap", "tap"],
];

const metricFromSeriesKey = (seriesKey) => {
  if (!seriesKey) return "power_kw";
  const lower = String(seriesKey).toLowerCase();
  const match = SERIES_METRIC_SUFFIXES.find(([suffix]) => lower.endsWith(`_${suffix}`));
  return match ? match[1] : "power_kw";
};

const formatDate = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const formatDisplayLabel = (rawLabel, mode) => {
  if (!rawLabel) return "";
  const parsed = new Date(rawLabel);
  // Check if date is invalid
  if (Number.isNaN(parsed.getTime())) return rawLabel;
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const dayName = days[parsed.getDay()];
  const monthNum = `${parsed.getMonth() + 1}`.padStart(2, "0");
  const monthName = months[parsed.getMonth()];
  const day = `${parsed.getDate()}`.padStart(2, "0");
  const year = parsed.getFullYear().toString().slice(-2);
  const fullYear = parsed.getFullYear();
  const hours = `${parsed.getHours()}`.padStart(2, "0");
  const minutes = `${parsed.getMinutes()}`.padStart(2, "0");

  if (mode === "second") {
    const seconds = `${parsed.getSeconds()}`.padStart(2, "0");
    return `${dayName} ${day}/${monthNum}/${year} ${hours}:${minutes}:${seconds}`;
  }
  if (mode === "minute" || mode === "hour") {
    return `${dayName} ${day}/${monthNum}/${year} ${hours}:${minutes}`;
  }
  if (mode === "month") {
    return `${monthName} ${fullYear}`;
  }
  if (mode === "year") {
    return `${fullYear}`;
  }
  return `${dayName} ${day}/${monthNum}/${year}`;
};

const parseHierarchy = (flatHierarchy) => {
  if (!flatHierarchy) return [];
  const rootNodes = [];

  // No need to use 'code' (meter_level)
  // Just build a flat list of machines but group by category if they have one
  const entries = Object.entries(flatHierarchy);

  // Identify any "LOAD" or "GRID" or "SOLAR" as main buckets
  // or just show everything linearly as requested "no need to use the code"
  entries.forEach(([id, data]) => {
    const name = data.name || id;
    const category =
      (typeof data === "object" ? data.category : "Unknown") || "Unknown";
    const color = (typeof data === "object" ? data.color : null) || "#808080";

    const node = {
      name,
      id,
      category,
      color,
      hierarchy_path: (typeof data === "object" ? data.hierarchy_path : null) || "",
      is_branch: typeof data === "object" ? !!data.is_branch : false,
      children: [],
      type: category?.toUpperCase() === "MACHINE" ? "machine" : "main",
    };

    rootNodes.push(node);
  });

  return rootNodes;
};

export default useHistoricalData;
